import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import MainSidebar from '../../components/ui/MainSidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import SignatureCanvas from './components/SignatureCanvas';
import { supervisorApprovalService } from '../../services/supervisorApprovalService';
import { workOrderService } from '../../services/workOrderService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import {
  getTallerWorkOrders,
  getMechanics,
  assignMechanic,
  updateWorkOrderStatus,
} from '../../services/quotationFlowService';
import { vehicleHistoryService } from '../../services/vehicleHistoryService';
import NavAcciones from '../../components/NavAcciones';

const WorkExecution = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [workOrder, setWorkOrder] = useState(null);
  const [quoteItems, setQuoteItems] = useState([]);
  const [itemProgress, setItemProgress] = useState({});
  
  // Taller: list of OTs from DB
  const [tallerWorkOrders, setTallerWorkOrders] = useState([]);
  const [loadingTaller, setLoadingTaller] = useState(false);
  const [tallerError, setTallerError] = useState('');
  const [selectedTallerWO, setSelectedTallerWO] = useState(null);

  // Mechanic assignment
  const [mechanics, setMechanics] = useState([]);
  const [assigningMechanic, setAssigningMechanic] = useState(false);
  const [selectedMechanicId, setSelectedMechanicId] = useState('');
  const [showAssignPanel, setShowAssignPanel] = useState(false);

  // Status update
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  // Supervisor approval state
  const [showApprovalSection, setShowApprovalSection] = useState(false);
  const [approvalData, setApprovalData] = useState({
    signatureData: null,
    approvalNotes: '',
    beforePhoto: null,
    afterPhoto: null,
    beforePhotoUrl: null,
    afterPhotoUrl: null
  });
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [approvalError, setApprovalError] = useState('');
  const [approvalSuccess, setApprovalSuccess] = useState(false);

  useEffect(() => {
    // Load data from navigation state
    if (location?.state) {
      setWorkOrder(location?.state?.workOrder);
      const items = location?.state?.quoteItems || [];
      setQuoteItems(items);
      
      // Initialize progress tracking for each item
      const initialProgress = {};
      items?.forEach((item, index) => {
        initialProgress[index] = {
          completed: false,
          startTime: null,
          endTime: null,
          notes: ''
        };
      });
      setItemProgress(initialProgress);
    }
    loadTallerData();
  }, []);

  useEffect(() => {
    loadTallerData();
  }, []);

  const loadTallerData = async () => {
    setLoadingTaller(true);
    setTallerError('');
    try {
      const [wos, mechs] = await Promise.all([
        getTallerWorkOrders(),
        getMechanics(),
      ]);
      setTallerWorkOrders(wos || []);
      setMechanics(mechs || []);
    } catch (err) {
      console.warn('[WorkExecution] loadTallerData error:', err?.message);
      setTallerError('No se pudieron cargar las órdenes de trabajo. Verifica tu conexión e intenta de nuevo.');
    } finally {
      setLoadingTaller(false);
    }
  };

  const handleToggleComplete = (index) => {
    setItemProgress(prev => ({
      ...prev,
      [index]: {
        ...prev?.[index],
        completed: !prev?.[index]?.completed,
        endTime: !prev?.[index]?.completed ? new Date()?.toISOString() : null
      }
    }));
  };

  const handleStartWork = (index) => {
    setItemProgress(prev => ({
      ...prev,
      [index]: {
        ...prev?.[index],
        startTime: new Date()?.toISOString()
      }
    }));
  };

  const handleBackToSupplier = () => {
    navigate('/supplier-coordination');
  };

  const handleSignatureSave = (signatureData) => {
    setApprovalData(prev => ({ ...prev, signatureData }));
    setApprovalError('');
  };

  const handleSignatureClear = () => {
    setApprovalData(prev => ({ ...prev, signatureData: null }));
  };

  const handlePhotoUpload = async (event, photoType) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    try {
      const photoUrl = URL.createObjectURL(file);
      
      if (photoType === 'before') {
        setApprovalData(prev => ({
          ...prev,
          beforePhoto: file,
          beforePhotoUrl: photoUrl
        }));
      } else {
        setApprovalData(prev => ({
          ...prev,
          afterPhoto: file,
          afterPhotoUrl: photoUrl
        }));
      }
      setApprovalError('');
    } catch (error) {
      console.error('Error handling photo:', error);
      setApprovalError('Error al cargar la foto');
    }
  };

  const handlePhotoRemove = (photoType) => {
    if (photoType === 'before') {
      if (approvalData?.beforePhotoUrl) {
        URL.revokeObjectURL(approvalData?.beforePhotoUrl);
      }
      setApprovalData(prev => ({
        ...prev,
        beforePhoto: null,
        beforePhotoUrl: null
      }));
    } else {
      if (approvalData?.afterPhotoUrl) {
        URL.revokeObjectURL(approvalData?.afterPhotoUrl);
      }
      setApprovalData(prev => ({
        ...prev,
        afterPhoto: null,
        afterPhotoUrl: null
      }));
    }
  };

  const handleSubmitApproval = async () => {
    // Validation
    if (!approvalData?.signatureData) {
      setApprovalError('Por favor, firme el documento');
      return;
    }
    if (!approvalData?.beforePhoto || !approvalData?.afterPhoto) {
      setApprovalError('Por favor, cargue ambas fotos (antes y después)');
      return;
    }
    if (!approvalData?.approvalNotes?.trim()) {
      setApprovalError('Por favor, agregue notas de aprobación');
      return;
    }

    setIsSubmittingApproval(true);
    setApprovalError('');

    try {
      // Upload photos
      const beforePhotoPath = await supervisorApprovalService?.uploadPhoto(
        approvalData?.beforePhoto,
        workOrder?.id,
        'before'
      );
      const afterPhotoPath = await supervisorApprovalService?.uploadPhoto(
        approvalData?.afterPhoto,
        workOrder?.id,
        'after'
      );

      // Create approval record
      const approval = await supervisorApprovalService?.create({
        workOrderId: workOrder?.id,
        supervisorId: userProfile?.id,
        status: 'approved',
        signatureData: approvalData?.signatureData,
        approvalNotes: approvalData?.approvalNotes,
        beforePhotoUrl: beforePhotoPath,
        afterPhotoUrl: afterPhotoPath,
        reviewedAt: new Date()?.toISOString()
      });

      // Update work order status
      await workOrderService?.updateStatus(workOrder?.id, 'completado');

      // Create timeline event
      await workOrderService?.createTimelineEvent({
        workOrderId: workOrder?.id,
        eventType: 'status_change',
        stage: 'completado',
        description: 'Trabajo completado y aprobado por supervisor',
        findings: approvalData?.approvalNotes,
        userId: userProfile?.id
      });

      setApprovalSuccess(true);
      setTimeout(() => {
        navigate('/workshop-dashboard', {
          state: { approvalSuccess: true, folio: workOrder?.id }
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting approval:', error);
      setApprovalError('Error al enviar la aprobación. Por favor, intente nuevamente.');
    } finally {
      setIsSubmittingApproval(false);
    }
  };

  const handleSelectTallerWO = (wo) => {
    setSelectedTallerWO(wo);
    setWorkOrder(wo);
    // Load scope_of_work as quoteItems
    const scope = wo?.scopeOfWork || wo?.scope_of_work || [];
    const items = scope
      ?.filter(s => s?.authorized !== false)
      ?.map((s, idx) => ({
        id: idx,
        description: s?.description || '',
        quantity: s?.quantity || 1,
        price: s?.price || 0,
        supplier: s?.supplier || '',
        type: s?.type || 'service',
      }));
    setQuoteItems(items);
    const initialProgress = {};
    items?.forEach((item, index) => {
      initialProgress[index] = { completed: false, startTime: null, endTime: null, notes: '' };
    });
    setItemProgress(initialProgress);
    setSelectedMechanicId(wo?.assignedToUserId || wo?.assigned_to_user_id || '');
    setShowAssignPanel(false);
    setApprovalSuccess(false);
    setApprovalError('');
  };

  const handleAssignMechanic = async () => {
    if (!selectedTallerWO?.id || !selectedMechanicId) return;
    setAssigningMechanic(true);
    try {
      const mech = mechanics?.find(m => m?.id === selectedMechanicId);
      const updated = await assignMechanic({
        workOrderId: selectedTallerWO?.id,
        mechanicUserId: selectedMechanicId,
        mechanicName: mech?.fullName || mech?.full_name || '',
      });
      if (updated) {
        setSelectedTallerWO(prev => ({ ...prev, ...updated }));
        setWorkOrder(prev => ({ ...prev, ...updated }));
        // Refresh list
        const wos = await getTallerWorkOrders();
        setTallerWorkOrders(wos || []);
      }
      setShowAssignPanel(false);
    } catch (err) {
      console.error('[WorkExecution] handleAssignMechanic error:', err);
      alert('Error al asignar mecánico: ' + err?.message);
    } finally {
      setAssigningMechanic(false);
    }
  };

  const handleUpdateWorkStatus = async (newStatus) => {
    if (!selectedTallerWO?.id) return;
    setUpdatingStatus(true);
    try {
      const updated = await updateWorkOrderStatus({
        workOrderId: selectedTallerWO?.id,
        newStatus,
      });
      if (updated) {
        setSelectedTallerWO(prev => ({ ...prev, ...updated }));
        setWorkOrder(prev => ({ ...prev, ...updated }));
        const wos = await getTallerWorkOrders();
        setTallerWorkOrders(wos || []);
      }

      // When work order is completed: close vehicle_history entries and apply preventive rules
      if (newStatus === 'completed') {
        const vehicleId = selectedTallerWO?.vehicleId || selectedTallerWO?.vehicle_id || selectedTallerWO?.vehicle?.id;
        const vehicleMileage = selectedTallerWO?.vehicle?.mileage || selectedTallerWO?.vehicle?.kilometraje || null;

        // Close existing DONE history entries for this work order
        await vehicleHistoryService?.closeByWorkOrderId(selectedTallerWO?.id);

        // Apply preventive service rules to generate next PENDING entries
        if (vehicleId) {
          await vehicleHistoryService?.applyPreventiveRules({
            vehicleId,
            workOrderId: selectedTallerWO?.id,
            serviceKmAtTime: vehicleMileage,
          });
        }
      }
    } catch (err) {
      console.error('[WorkExecution] handleUpdateWorkStatus error:', err);
      alert('Error al actualizar estado: ' + err?.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const completedItems = Object.values(itemProgress)?.filter(p => p?.completed)?.length || 0;
  const totalItems = quoteItems?.length || 0;
  const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const allItemsCompleted = totalItems > 0 && completedItems === totalItems;

  return (
    <>
      <Helmet>
        <title>Ejecución de Trabajo - GearControl</title>
      </Helmet>
      <div className="flex h-screen overflow-hidden bg-background">
        <MainSidebar collapsed={sidebarCollapsed} onToggle={setSidebarCollapsed} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
              <NavAcciones
                onRegresar={() => navigate(-1)}
                onSalir={async () => { await supabase?.auth?.signOut(); navigate('/signin'); }}
                label="Ejecución de Trabajo"
              />
              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Icon name="Wrench" size={32} className="text-accent" />
                    <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground">
                      Ejecución de Trabajo
                    </h1>
                  </div>
                  <Button
                    variant="outline"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={handleBackToSupplier}
                  >
                    Volver
                  </Button>
                </div>
                <p className="text-muted-foreground">
                  Gestione el progreso de trabajo y documentación de servicios
                </p>
              </div>

              {/* TALLER: OT List from DB */}
              <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="ClipboardList" size={24} className="text-primary" />
                  <h2 className="text-xl font-heading font-semibold text-foreground">Órdenes de Trabajo — Taller</h2>
                  <button
                    onClick={loadTallerData}
                    className="ml-auto p-2 rounded-lg hover:bg-muted transition-colors"
                    title="Actualizar"
                  >
                    <Icon name="RefreshCw" size={16} className={loadingTaller ? 'animate-spin text-primary' : 'text-muted-foreground'} />
                  </button>
                </div>
                {loadingTaller ? (
                  <div className="flex items-center gap-2 py-4">
                    <Icon name="Loader2" size={18} className="text-primary animate-spin" />
                    <span className="text-sm text-muted-foreground">Cargando órdenes...</span>
                  </div>
                ) : tallerError ? (
                  <div className="flex items-center gap-3 py-4 px-3 bg-red-50 border border-red-200 rounded-lg">
                    <Icon name="AlertCircle" size={18} className="text-red-500 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm text-red-700">{tallerError}</p>
                    </div>
                    <button
                      onClick={loadTallerData}
                      className="text-xs text-red-600 underline hover:text-red-800 flex-shrink-0"
                    >
                      Reintentar
                    </button>
                  </div>
                ) : tallerWorkOrders?.length === 0 ? (
                  <div className="text-center py-8">
                    <Icon name="CheckCircle2" size={40} className="text-success mx-auto mb-2" />
                    <p className="text-muted-foreground">No hay órdenes pendientes en taller</p>
                    <p className="text-xs text-muted-foreground mt-1">Las OTs aparecen aquí cuando son aprobadas por el cliente</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {tallerWorkOrders?.map(wo => (
                      <button
                        key={wo?.id}
                        onClick={() => handleSelectTallerWO(wo)}
                        className={`text-left p-4 rounded-lg border transition-all hover:border-primary/50 ${
                          selectedTallerWO?.id === wo?.id ? 'border-primary bg-primary/5' : 'border-border'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-sm font-bold text-primary">{wo?.folioOt || wo?.folio_ot}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                            wo?.workStatus === 'in_progress' ? 'bg-blue-100 text-blue-700' :
                            wo?.workStatus === 'assigned'? 'bg-yellow-100 text-yellow-700' : 'bg-muted text-muted-foreground'
                          }`}>
                            {wo?.workStatus === 'in_progress' ? 'En Proceso' :
                             wo?.workStatus === 'assigned' ? 'Asignado' : 'Creado'}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-foreground">
                          {wo?.vehicle?.marca} {wo?.vehicle?.modelo} {wo?.vehicle?.year}
                        </p>
                        <p className="text-xs text-muted-foreground">{wo?.client?.nombre}</p>
                        {wo?.vehicle?.vin && (
                          <p className="text-xs text-muted-foreground mt-1">VIN: {wo?.vehicle?.vin}</p>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Selected OT Detail */}
              {selectedTallerWO && (
                <>
                  {/* OT Info */}
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="FileText" size={24} className="text-primary" />
                      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                        Detalle OT: {selectedTallerWO?.folioOt || selectedTallerWO?.folio_ot}
                      </h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Cliente</p>
                        <p className="text-sm font-semibold text-foreground">{selectedTallerWO?.client?.nombre || '—'}</p>
                        {selectedTallerWO?.client?.telefono && (
                          <p className="text-xs text-muted-foreground">{selectedTallerWO?.client?.telefono}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Vehículo</p>
                        <p className="text-sm font-semibold text-foreground">
                          {selectedTallerWO?.vehicle?.marca} {selectedTallerWO?.vehicle?.modelo} {selectedTallerWO?.vehicle?.year}
                        </p>
                        {selectedTallerWO?.vehicle?.color && (
                          <p className="text-xs text-muted-foreground">{selectedTallerWO?.vehicle?.color}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">VIN / Placas</p>
                        <p className="text-sm font-semibold text-foreground">{selectedTallerWO?.vehicle?.vin || '—'}</p>
                        <p className="text-xs text-muted-foreground">{selectedTallerWO?.vehicle?.placas || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Estado</p>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800`}>
                          {selectedTallerWO?.workStatus === 'in_progress' ? 'En Proceso' :
                           selectedTallerWO?.workStatus === 'completed' ? 'Completado' :
                           selectedTallerWO?.workStatus === 'assigned' ? 'Asignado' : 'Creado'}
                        </span>
                      </div>
                    </div>
                    {(selectedTallerWO?.vehicleId || selectedTallerWO?.vehicle_id || selectedTallerWO?.vehicle?.id) && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <button
                          onClick={() => navigate(`/vehicle-history/${selectedTallerWO?.vehicleId || selectedTallerWO?.vehicle_id || selectedTallerWO?.vehicle?.id}`)}
                          className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium transition-colors"
                        >
                          <Icon name="History" size={16} />
                          Ver Historial del Vehículo
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Mechanic Assignment */}
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Icon name="UserCheck" size={24} className="text-accent" />
                        <h2 className="text-xl font-heading font-semibold text-foreground">Asignación de Mecánico</h2>
                      </div>
                      {!showAssignPanel && (
                        <Button
                          variant="outline"
                          iconName="UserPlus"
                          iconPosition="left"
                          onClick={() => setShowAssignPanel(true)}
                          size="sm"
                        >
                          {selectedTallerWO?.assignedToUserId ? 'Reasignar' : 'Asignar'}
                        </Button>
                      )}
                    </div>

                    {selectedTallerWO?.assignedToUserId && !showAssignPanel && (
                      <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="User" size={20} className="text-primary" />
                        <div>
                          <p className="text-sm font-medium text-foreground">
                            {mechanics?.find(m => m?.id === selectedTallerWO?.assignedToUserId)?.fullName ||
                             mechanics?.find(m => m?.id === selectedTallerWO?.assignedToUserId)?.full_name ||
                             'Mecánico asignado'}
                          </p>
                          {selectedTallerWO?.assignedAt && (
                            <p className="text-xs text-muted-foreground">
                              Asignado: {new Date(selectedTallerWO?.assignedAt)?.toLocaleString('es-MX')}
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {showAssignPanel && (
                      <div className="space-y-3">
                        <select
                          value={selectedMechanicId}
                          onChange={e => setSelectedMechanicId(e?.target?.value)}
                          className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        >
                          <option value="">Seleccionar mecánico...</option>
                          {mechanics?.map(m => (
                            <option key={m?.id} value={m?.id}>
                              {m?.fullName || m?.full_name} ({m?.email})
                            </option>
                          ))}
                        </select>
                        {mechanics?.length === 0 && (
                          <p className="text-xs text-muted-foreground">No hay mecánicos disponibles (usuarios con rol "taller")</p>
                        )}
                        <div className="flex gap-3">
                          <Button
                            variant="default"
                            iconName="UserCheck"
                            iconPosition="left"
                            onClick={handleAssignMechanic}
                            disabled={!selectedMechanicId || assigningMechanic}
                            loading={assigningMechanic}
                          >
                            Confirmar Asignación
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => setShowAssignPanel(false)}
                            disabled={assigningMechanic}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Status Controls */}
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="Activity" size={24} className="text-primary" />
                      <h2 className="text-xl font-heading font-semibold text-foreground">Control de Estado</h2>
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {selectedTallerWO?.workStatus === 'created' || selectedTallerWO?.workStatus === 'assigned' ? (
                        <Button
                          variant="default"
                          iconName="Play"
                          iconPosition="left"
                          onClick={() => handleUpdateWorkStatus('in_progress')}
                          disabled={updatingStatus}
                          loading={updatingStatus}
                        >
                          Iniciar Trabajo
                        </Button>
                      ) : null}
                      {selectedTallerWO?.workStatus === 'in_progress' ? (
                        <Button
                          variant="success"
                          iconName="CheckCircle"
                          iconPosition="left"
                          onClick={() => handleUpdateWorkStatus('completed')}
                          disabled={updatingStatus}
                          loading={updatingStatus}
                        >
                          Marcar Completado
                        </Button>
                      ) : null}
                      {selectedTallerWO?.workStatus === 'completed' ? (
                        <div className="flex items-center gap-2 text-success">
                          <Icon name="CheckCircle2" size={20} />
                          <span className="text-sm font-medium">Trabajo completado</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              )}

              {/* Work Order Info (from navigation state - legacy) */}
              {workOrder && !selectedTallerWO && (
                <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                  <div className="flex items-center gap-2 mb-4">
                    <Icon name="FileText" size={24} className="text-primary" />
                    <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                      Información de Orden de Trabajo
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Folio</p>
                      <p className="text-base font-semibold text-foreground">{workOrder?.id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Vehículo</p>
                      <p className="text-base font-semibold text-foreground">{workOrder?.vehicle}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Cliente</p>
                      <p className="text-base font-semibold text-foreground">{workOrder?.client}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Estado</p>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        En Proceso
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Progress Overview */}
              <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="TrendingUp" size={24} className="text-accent" />
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                    Progreso General
                  </h2>
                </div>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-foreground">
                        {completedItems} de {totalItems} items completados
                      </span>
                      <span className="text-sm font-semibold text-accent">
                        {progressPercentage?.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-3">
                      <div
                        className="bg-accent h-3 rounded-full transition-all duration-300"
                        style={{ width: `${progressPercentage}%` }}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Total Items</p>
                      <p className="text-2xl font-bold text-foreground">{totalItems}</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3">
                      <p className="text-xs text-muted-foreground mb-1">Completados</p>
                      <p className="text-2xl font-bold text-green-600">{completedItems}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Work Items List */}
              <div className="bg-card rounded-lg border border-border p-4 md:p-6 mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <Icon name="List" size={24} className="text-primary" />
                  <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                    Items de Trabajo
                  </h2>
                </div>
                <div className="space-y-4">
                  {quoteItems?.map((item, index) => {
                    const progress = itemProgress?.[index] || {};
                    const isCompleted = progress?.completed;
                    const isStarted = progress?.startTime;

                    return (
                      <div
                        key={index}
                        className={`border rounded-lg p-4 transition-all ${
                          isCompleted
                            ? 'border-green-500 bg-green-50/50'
                            : isStarted
                            ? 'border-blue-500 bg-blue-50/50' :'border-border bg-card'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          {/* Checkbox */}
                          <div className="flex-shrink-0 pt-1">
                            <button
                              onClick={() => handleToggleComplete(index)}
                              className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                                isCompleted
                                  ? 'bg-green-600 border-green-600' :'border-muted-foreground hover:border-accent'
                              }`}
                            >
                              {isCompleted && (
                                <Icon name="Check" size={16} className="text-white" />
                              )}
                            </button>
                          </div>

                          {/* Item Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between gap-4 mb-2">
                              <div className="flex-1">
                                <h3 className="text-base font-semibold text-foreground mb-1">
                                  {item?.description}
                                </h3>
                                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
                                  <span className="flex items-center gap-1">
                                    <Icon name="Package" size={14} />
                                    Cantidad: {item?.quantity || 1}
                                  </span>
                                  {item?.supplier && (
                                    <span className="flex items-center gap-1">
                                      <Icon name="Truck" size={14} />
                                      {item?.supplier}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="flex-shrink-0">
                                {isCompleted ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Completado
                                  </span>
                                ) : isStarted ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    En Progreso
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Pendiente
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Action Button */}
                            {!isCompleted && !isStarted && (
                              <div className="mt-3">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  iconName="Play"
                                  iconPosition="left"
                                  onClick={() => handleStartWork(index)}
                                >
                                  Iniciar Item
                                </Button>
                              </div>
                            )}

                            {/* Time Tracking */}
                            {isStarted && (
                              <div className="mt-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Icon name="Clock" size={12} />
                                  Iniciado: {new Date(progress?.startTime)?.toLocaleString('es-MX')}
                                </span>
                                {isCompleted && progress?.endTime && (
                                  <span className="flex items-center gap-1 mt-1">
                                    <Icon name="CheckCircle" size={12} />
                                    Completado: {new Date(progress?.endTime)?.toLocaleString('es-MX')}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Summary Footer */}
                {quoteItems?.length === 0 && (
                  <div className="text-center py-8 text-muted-foreground">
                    <Icon name="AlertCircle" size={48} className="mx-auto mb-3 opacity-50" />
                    <p>No hay items de trabajo cargados</p>
                  </div>
                )}
              </div>

              {/* Supervisor Approval Section */}
              {allItemsCompleted && (
                <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Icon name="CheckCircle" size={24} className="text-green-600" />
                      <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                        Aprobación de Supervisor
                      </h2>
                    </div>
                    {!showApprovalSection && (
                      <Button
                        variant="default"
                        iconName="FileSignature"
                        onClick={() => setShowApprovalSection(true)}
                      >
                        Iniciar Aprobación
                      </Button>
                    )}
                  </div>

                  {showApprovalSection && (
                    <div className="space-y-6 mt-6">
                      {/* Success Message */}
                      {approvalSuccess && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <Icon name="CheckCircle" size={20} className="text-green-600" />
                            <p className="text-green-800 font-medium">
                              Aprobación enviada exitosamente. Redirigiendo...
                            </p>
                          </div>
                        </div>
                      )}

                      {/* Error Message */}
                      {approvalError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <Icon name="AlertCircle" size={20} className="text-red-600" />
                            <p className="text-red-800">{approvalError}</p>
                          </div>
                        </div>
                      )}

                      {/* Digital Signature */}
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Firma Digital <span className="text-red-500">*</span>
                        </label>
                        <SignatureCanvas
                          onSave={handleSignatureSave}
                          onClear={handleSignatureClear}
                        />
                      </div>

                      {/* Photo Upload Section */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Before Photo */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Foto Antes <span className="text-red-500">*</span>
                          </label>
                          <div className="space-y-3">
                            {!approvalData?.beforePhotoUrl ? (
                              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handlePhotoUpload(e, 'before')}
                                  className="hidden"
                                  id="before-photo-input"
                                />
                                <label
                                  htmlFor="before-photo-input"
                                  className="cursor-pointer flex flex-col items-center gap-2"
                                >
                                  <Icon name="Upload" size={32} className="text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    Click para cargar foto
                                  </p>
                                </label>
                              </div>
                            ) : (
                              <div className="relative">
                                <img
                                  src={approvalData?.beforePhotoUrl}
                                  alt="Foto antes del trabajo"
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                                <button
                                  onClick={() => handlePhotoRemove('before')}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                                >
                                  <Icon name="X" size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* After Photo */}
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Foto Después <span className="text-red-500">*</span>
                          </label>
                          <div className="space-y-3">
                            {!approvalData?.afterPhotoUrl ? (
                              <div className="border-2 border-dashed border-border rounded-lg p-6 text-center">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={(e) => handlePhotoUpload(e, 'after')}
                                  className="hidden"
                                  id="after-photo-input"
                                />
                                <label
                                  htmlFor="after-photo-input"
                                  className="cursor-pointer flex flex-col items-center gap-2"
                                >
                                  <Icon name="Upload" size={32} className="text-muted-foreground" />
                                  <p className="text-sm text-muted-foreground">
                                    Click para cargar foto
                                  </p>
                                </label>
                              </div>
                            ) : (
                              <div className="relative">
                                <img
                                  src={approvalData?.afterPhotoUrl}
                                  alt="Foto después del trabajo"
                                  className="w-full h-48 object-cover rounded-lg"
                                />
                                <button
                                  onClick={() => handlePhotoRemove('after')}
                                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600"
                                >
                                  <Icon name="X" size={16} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Approval Notes */}
                      <div>
                        <Input
                          label="Notas de Aprobación"
                          type="text"
                          placeholder="Ingrese observaciones o comentarios sobre el trabajo realizado"
                          value={approvalData?.approvalNotes}
                          onChange={(e) =>
                            setApprovalData(prev => ({
                              ...prev,
                              approvalNotes: e?.target?.value
                            }))
                          }
                          required
                        />
                      </div>

                      {/* Submit Button */}
                      <div className="flex items-center gap-3 pt-4">
                        <Button
                          variant="default"
                          iconName="CheckCircle"
                          onClick={handleSubmitApproval}
                          loading={isSubmittingApproval}
                          disabled={isSubmittingApproval || approvalSuccess}
                        >
                          Enviar Aprobación
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowApprovalSection(false)}
                          disabled={isSubmittingApproval || approvalSuccess}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkExecution;